import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, Form } from "react-bootstrap";
import { FiLock, FiChevronUp, FiChevronDown } from "react-icons/fi";

import Spinner from "../Components/Spinner";
import { ToastComponent } from "../Components/ToastComponent";
import EmptyData from "../../components/EmptyData";
import SearchInputField from "../Components/Forms/SearchInputField";
import SearchInputDocument from "../Components/Forms/SearchInputDocument";

import { clientService } from "../../Services/client.service";
import { usersService } from "../../Services/users.service";
import { menuPermissionsService } from "../../Services/menuPermissions.service";

import "./UserPermissions.css";

const SEARCH_MODE = {
  NAME: "NAME",
  DOCUMENT: "DOCUMENT",
};

function buildPermissionTree(items) {
  if (!Array.isArray(items)) return [];

  const groups = [];
  let currentGroup = null;

  items.forEach((rawItem) => {
    const menuId = rawItem.menuId ?? rawItem.MenuId;
    const level = rawItem.level ?? rawItem.Level;
    const description = rawItem.description ?? rawItem.Description;
    const active = rawItem.active ?? rawItem.Active;

    if (level === 1) {
      currentGroup = { menuId, description, children: [] };
      groups.push(currentGroup);
    } else if (currentGroup) {
      currentGroup.children.push({
        menuId,
        description,
        checked: active === "S",
        blocked: active === "B",
      });
    }
  });

  return groups.map((group) => ({
    ...group,
    checked: group.children.length > 0 && group.children.some((child) => child.checked),
  }));
}

function flattenChecks(groups) {
  const map = new Map();
  groups.forEach((group) => {
    map.set(group.menuId, group.checked);
    group.children.forEach((child) => {
      map.set(child.menuId, child.checked);
    });
  });
  return map;
}

// Remove acentos pra comparação de busca ("créditos" == "creditos").
const normalizeText = (value) =>
  (value ?? "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const formatDocument = (doc) => {
  const digits = (doc ?? "").toString().replace(/\D/g, "");
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  }
  return doc ?? "";
};

export default function UserPermissions() {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch({ type: "title", value: "Permissão por Usuário" });
  }, [dispatch]);

  const [searchMode, setSearchMode] = useState(SEARCH_MODE.NAME);
  const [searchValue, setSearchValue] = useState("");
  const [documentValue, setDocumentValue] = useState("");
  const [isSearchingClients, setIsSearchingClients] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState("");
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [originalChecks, setOriginalChecks] = useState(new Map());
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const [moduleFilter, setModuleFilter] = useState("");
  const [toastParams, setToastParams] = useState("");

  const showToast = (title, body, color) => {
    setToastParams(JSON.stringify({ show: true, title, body, color }));
  };

  const handleSearchClients = async (e) => {
    e?.preventDefault?.();

    const payload =
      searchMode === SEARCH_MODE.NAME
        ? { ClientName: searchValue }
        : { Doc: documentValue.replace(/\D/g, "") };

    if (!payload.ClientName && !payload.Doc) return;

    setIsSearchingClients(true);
    setSelectedClient(null);
    setSelectedUser(null);
    setUsers([]);
    setUserFilter("");
    setPermissionGroups([]);
    setOriginalChecks(new Map());

    try {
      const response = await clientService.search(payload);
      const items = Array.isArray(response) ? response : response?.items || (response ? [response] : []);
      setClients(items);
    } catch (error) {
      showToast("Erro", "Erro ao buscar clientes", "Danger");
    } finally {
      setIsSearchingClients(false);
    }
  };

  const handleSelectClient = async (client) => {
    setSelectedClient(client);
    setSelectedUser(null);
    setPermissionGroups([]);
    setOriginalChecks(new Map());
    setUserFilter("");
    setIsLoadingUsers(true);

    try {
      const clientId = client.clientId;
      const response = await usersService.byClient(clientId);
      setUsers(Array.isArray(response) ? response : response ? [response] : []);
    } catch (error) {
      showToast("Erro", "Erro ao buscar usuários do cliente", "Danger");
      setUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    setIsLoadingPermissions(true);
    setPermissionGroups([]);
    setOriginalChecks(new Map());
    setCollapsedGroups(new Set());
    setModuleFilter("");

    try {
      const userId = user.userId ?? user.UserId;
      const response = await menuPermissionsService.fetchUserPermissions(userId);
      const tree = buildPermissionTree(response);
      setPermissionGroups(tree);
      setOriginalChecks(flattenChecks(tree));
    } catch (error) {
      showToast("Erro", "Erro ao carregar permissões do usuário", "Danger");
    } finally {
      setIsLoadingPermissions(false);
    }
  };

  const toggleChild = (groupIndex, childIndex, checked) => {
    setPermissionGroups((currentGroups) =>
      currentGroups.map((group, gIdx) => {
        if (gIdx !== groupIndex) return group;

        const children = group.children.map((child, cIdx) =>
          cIdx === childIndex ? { ...child, checked } : child
        );

        return {
          ...group,
          children,
          checked: children.some((child) => child.checked),
        };
      })
    );
  };

  const toggleGroup = (groupIndex, checked) => {
    setPermissionGroups((currentGroups) =>
      currentGroups.map((group, gIdx) => {
        if (gIdx !== groupIndex) return group;

        return {
          ...group,
          checked,
          // Itens bloqueados (blocked) não são afetados pelo toggle do pai —
          // eles são controlados pelo nível de acesso/perfil, não por aqui.
          children: group.children.map((child) =>
            child.blocked ? child : { ...child, checked }
          ),
        };
      })
    );
  };

  const toggleCollapse = (menuId) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(menuId)) {
        next.delete(menuId);
      } else {
        next.add(menuId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    const userId = selectedUser.userId ?? selectedUser.UserId;

    const currentChecks = flattenChecks(permissionGroups);
    const changes = [];
    currentChecks.forEach((checked, menuId) => {
      if (originalChecks.get(menuId) !== checked) {
        changes.push({ menuId, active: checked ? "S" : "N" });
      }
    });

    if (changes.length === 0) {
      showToast("Atenção", "Nenhuma alteração para salvar", "Warning");
      return;
    }

    setIsSaving(true);
    try {
      let allOk = true;
      const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

for (const change of changes) {
  // eslint-disable-next-line no-await-in-loop
  const result = await menuPermissionsService.updateUserPermission(userId, change.menuId, change.active);
  if (!result) allOk = false;
  // eslint-disable-next-line no-await-in-loop
  await sleep(250); // pausa entre chamadas — teste pra ver se resolve problema de lote
}

      await handleSelectUser(selectedUser);

      if (allOk) {
        showToast("Sucesso", "Permissões salvas com sucesso", "Success");
      } else {
        showToast("Atenção", "Algumas permissões podem não ter sido salvas", "Warning");
      }
    } catch (error) {
      showToast("Erro", "Erro ao salvar permissões", "Danger");
    } finally {
      setIsSaving(false);
    }
  };

  // Filtro local (front) da lista de usuários do cliente já carregada —
  // não bate no backend de novo, só reduz o que já veio.
  const visibleUsers = useMemo(() => {
    const term = normalizeText(userFilter.trim());
    if (!term) return users;

    return users.filter((user) => {
      const name = user.userName ?? user.UserName ?? "";
      return normalizeText(name).includes(term);
    });
  }, [users, userFilter]);

  // Filtra por nome do módulo (grupo) ou por nome de uma permissão dentro
  // dele. Se o termo bater no grupo, mantém todos os filhos; se bater só em
  // algum filho, mostra o grupo com apenas os filhos que baterem.
  const visibleGroups = useMemo(() => {
    const term = normalizeText(moduleFilter.trim());
    if (!term) return permissionGroups;

    return permissionGroups
      .map((group) => {
        const groupMatches = normalizeText(group.description).includes(term);
        if (groupMatches) return group;

        const matchingChildren = group.children.filter((child) =>
          normalizeText(child.description).includes(term)
        );

        if (matchingChildren.length === 0) return null;
        return { ...group, children: matchingChildren };
      })
      .filter(Boolean);
  }, [permissionGroups, moduleFilter]);

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />

      {/* Busca de cliente */}
      <div className="card p-3 mb-3">
        <h5 className="mb-3">Buscar Cliente</h5>

        <div className="d-flex gap-3 mb-3">
          <Form.Check
            type="radio"
            label="Nome"
            name="searchMode"
            checked={searchMode === SEARCH_MODE.NAME}
            onChange={() => setSearchMode(SEARCH_MODE.NAME)}
          />
          <Form.Check
            type="radio"
            label="CPF/CNPJ"
            name="searchMode"
            checked={searchMode === SEARCH_MODE.DOCUMENT}
            onChange={() => setSearchMode(SEARCH_MODE.DOCUMENT)}
          />
        </div>

        <Form onSubmit={handleSearchClients}>
          <div className="d-flex gap-2 align-items-start">
            <div className="flex-grow-1">
              {searchMode === SEARCH_MODE.NAME ? (
                <SearchInputField
                  key="client-name-search"
                  name="clientName"
                  label="Nome do cliente"
                  type="text"
                  tabIndex="1"
                  onChange={(e) => setSearchValue(e.target.value)}
                />
              ) : (
                <SearchInputDocument
                  name="document"
                  label="CPF/CNPJ"
                  value={documentValue}
                  onChange={(e) => setDocumentValue(e.target.value)}
                />
              )}
            </div>
            <Button type="submit" variant="primary" disabled={isSearchingClients}>
              Buscar
            </Button>
          </div>
        </Form>

        {isSearchingClients ? (
          <Spinner />
        ) : (
          clients.length > 0 && (
            <div className="mt-3" style={{ maxHeight: 240, overflowY: "auto" }}>
              {clients.map((client) => {
                const clientId = client.clientId;
                const isSelected = selectedClient && selectedClient.clientId === clientId;

                return (
                  <div
                    key={clientId}
                    className={`border rounded px-3 py-2 mb-2 ${isSelected ? "border-primary" : ""}`}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSelectClient(client)}
                  >
                    {client.clientName ?? "Cliente"}
                    {client.doc && (
                      <span className="text-muted ms-2">({formatDocument(client.doc)})</span>
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>

      {/* Usuários do cliente */}
      {selectedClient && (
        <div className="card p-3 mb-3">
          <h5 className="mb-3">Usuários de {selectedClient.clientName}</h5>

          {isLoadingUsers ? (
            <Spinner />
          ) : users.length === 0 ? (
            <EmptyData />
          ) : (
            <>
              <div className="mb-3">
                <SearchInputField
                  key={`user-filter-${selectedClient.clientId}`}
                  name="userFilter"
                  label="Filtrar usuário"
                  type="text"
                  tabIndex="1"
                  onChange={(e) => setUserFilter(e.target.value)}
                />
              </div>

              {visibleUsers.length === 0 ? (
                <p className="text-muted mb-0">Nenhum usuário encontrado para "{userFilter}".</p>
              ) : (
                <div style={{ maxHeight: 240, overflowY: "auto" }}>
                  {visibleUsers.map((user) => {
                    const userId = user.userId ?? user.UserId;
                    const isSelected = selectedUser && (selectedUser.userId ?? selectedUser.UserId) === userId;

                    return (
                      <div
                        key={userId}
                        className={`border rounded px-3 py-2 mb-2 ${isSelected ? "border-primary" : ""}`}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSelectUser(user)}
                      >
                        {user.userName ?? user.UserName ?? "Usuário"}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Permissões — layout em cards por módulo */}
      {selectedUser && (
        <div className="perm-panel">
          <div className="perm-panel-header">
            <h5 className="perm-panel-title">
              Permissões de{" "}
              <strong>{(selectedUser.userName ?? selectedUser.UserName ?? "").toUpperCase()}</strong>
            </h5>
            <Button
              className="perm-save-btn"
              onClick={handleSave}
              disabled={isSaving || isLoadingPermissions}
            >
              {isSaving ? "Salvando..." : "Salvar"}
            </Button>
          </div>

          <div className="perm-filter-wrap">
            <input
              type="text"
              className="perm-filter-input"
              placeholder="Filtrar por nome do módulo ou permissão..."
              value={moduleFilter}
              onChange={(e) => setModuleFilter(e.target.value)}
            />
          </div>

          {isLoadingPermissions ? (
            <Spinner />
          ) : permissionGroups.length === 0 ? (
            <EmptyData />
          ) : visibleGroups.length === 0 ? (
            <p className="perm-empty-filter">Nenhum módulo ou permissão encontrado para "{moduleFilter}".</p>
          ) : (
            <div className="perm-grid">
              {visibleGroups.map((group) => {
                const groupIndex = permissionGroups.findIndex((g) => g.menuId === group.menuId);
                const isCollapsed = collapsedGroups.has(group.menuId);

                return (
                  <div className="perm-card" key={group.menuId}>
                    <div className="perm-card-header">
                      <label className="perm-check-row perm-card-title">
                        <input
                          type="checkbox"
                          checked={group.checked}
                          onChange={(e) => toggleGroup(groupIndex, e.target.checked)}
                        />
                        <span>{group.description}</span>
                      </label>

                      <button
                        type="button"
                        className="perm-collapse-btn"
                        onClick={() => toggleCollapse(group.menuId)}
                      >
                        {isCollapsed ? "expandir" : "recolher"}
                        {isCollapsed ? <FiChevronDown /> : <FiChevronUp />}
                      </button>
                    </div>

                    {!isCollapsed && (
                      <div className="perm-card-body">
                        {group.children.map((child) => {
                          const childIndex = permissionGroups[groupIndex].children.findIndex(
                            (c) => c.menuId === child.menuId
                          );

                          return (
                            <label
                              key={child.menuId}
                              className={`perm-check-row perm-item ${child.blocked ? "perm-item-blocked" : ""}`}
                              title={
                                child.blocked
                                  ? "Bloqueado pelo perfil de acesso — não pode ser alterado por usuário"
                                  : undefined
                              }
                            >
                              <input
                                type="checkbox"
                                checked={child.checked}
                                disabled={child.blocked}
                                onChange={(e) => toggleChild(groupIndex, childIndex, e.target.checked)}
                              />
                              {child.blocked && <FiLock className="perm-lock-icon" />}
                              <span>{child.description}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
